/**
 * Formatação de dados na voz do sistema (docs/styleguide/04-escrita.md):
 * valor em R$ com separador de milhar, data em dd/mm, tempo relativo até 7 dias,
 * nome de pessoa em caixa de título — porque o nome vai para o contrato.
 */

const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
})

const BRL_INTEGER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** `formatCurrency(3000)` → "R$ 3.000,00"; com `{ compact: true }` → "R$ 3.000". */
export function formatCurrency(value: number, options: { compact?: boolean } = {}) {
  const formatted =
    options.compact && Number.isInteger(value) ? BRL_INTEGER.format(value) : BRL.format(value)
  // Intl usa espaço não separável entre "R$" e o número; padronizamos em espaço comum.
  return formatted.replace(/[\u00a0\u202f]/g, ' ')
}

function pad(n: number) {
  return String(n).padStart(2, '0')
}

/** `formatDate(d)` → "05/09/2026"; `formatDate(d, { year: false })` → "05/09". */
export function formatDate(date: Date, options: { year?: boolean } = {}) {
  const dd = pad(date.getDate())
  const mm = pad(date.getMonth() + 1)
  if (options.year === false) return `${dd}/${mm}`
  return `${dd}/${mm}/${date.getFullYear()}`
}

/** "09:41" */
export function formatTime(date: Date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * Tempo relativo para o usuário: "agora", "há 2 minutos", "há 3 horas", "há 3 dias".
 * Acima de 7 dias vira data absoluta — timestamp de log nunca aparece na interface.
 */
export function formatRelative(date: Date, now: Date = new Date()) {
  const diff = now.getTime() - date.getTime()
  if (diff < MINUTE) return 'agora'
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE)
    return `há ${m} ${m === 1 ? 'minuto' : 'minutos'}`
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR)
    return `há ${h} ${h === 1 ? 'hora' : 'horas'}`
  }
  const d = Math.floor(diff / DAY)
  if (d <= 7) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`
  return formatDate(date)
}

/** Dias corridos em aberto desde `since` — a coluna "em aberto" da lista de assinaturas. */
export function daysOpen(since: Date, now: Date = new Date()) {
  return Math.max(0, Math.floor((now.getTime() - since.getTime()) / DAY))
}

const LOWERCASE_PARTICLES = new Set([
  'da',
  'das',
  'de',
  'do',
  'dos',
  'e',
  'di',
  'du',
  'del',
  'van',
  'von',
])

/**
 * Nome de pessoa em caixa de título, mesmo quando digitado em minúscula ou maiúscula.
 * Partículas ("da", "de", "dos") ficam minúsculas, exceto na primeira posição.
 */
export function titleCase(name: string) {
  return name
    .trim()
    .toLocaleLowerCase('pt-BR')
    .split(/\s+/)
    .map((word, index) => {
      if (index > 0 && LOWERCASE_PARTICLES.has(word)) return word
      return word
        .split('-')
        .map((part) => (part ? part.charAt(0).toLocaleUpperCase('pt-BR') + part.slice(1) : part))
        .join('-')
    })
    .join(' ')
}

/** "03709333113" → "037.093.331-13" (aceita entrada parcial durante a digitação). */
export function formatCPF(digits: string) {
  const d = digits.replace(/\D/g, '').slice(0, 11)
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')
}

/**
 * "01996766000130" → "01.996.766/0001-30"; aceita a raiz alfanumérica ("12abc34501de35" →
 * "12.ABC.345/01DE-35") e entrada parcial durante a digitação.
 */
export function formatCNPJ(value: string) {
  const d = value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 14)
  return d
    .replace(/^(.{2})(.)/, '$1.$2')
    .replace(/^(.{2})\.(.{3})(.)/, '$1.$2.$3')
    .replace(/^(.{2})\.(.{3})\.(.{3})(.)/, '$1.$2.$3/$4')
    .replace(/^(.{2})\.(.{3})\.(.{3})\/(.{4})(.)/, '$1.$2.$3/$4-$5')
}

/** "64992452797" → "64 99245-2797"; com DDI "5564992452797" → "+55 64 99245-2797". */
export function formatPhone(digits: string) {
  const d = digits.replace(/\D/g, '')
  const local = d.length > 11 ? d.slice(-11) : d
  const ddi = d.length > 11 ? `+${d.slice(0, d.length - 11)} ` : ''
  if (local.length <= 2) return ddi + local
  const ddd = local.slice(0, 2)
  const rest = local.slice(2)
  if (rest.length <= 4) return `${ddi}${ddd} ${rest}`
  const split = rest.length > 8 ? 5 : 4
  return `${ddi}${ddd} ${rest.slice(0, split)}-${rest.slice(split)}`
}

/** Plural simples para contadores: `plural(3, 'documento')` → "3 documentos". */
export function plural(count: number, singular: string, pluralForm = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralForm}`
}
